import {
  ExpandLess,
  ExpandMore,
  Folder,
  FolderOpen,
  RuleFolder,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useCallback } from 'react';

import { useWorkflows } from '~/data/workflows';
import { OrgUnitLabel } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/OrgUnitLabel';
import {
  NestedOrgTreeUnit,
  useWorkflowConfigUnitSelector,
} from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigUnitSelector/useWorkflowConfigUnitSelector';
import { theme } from '~/theme';

type PropsType = {
  onUnitSelect: (unitId: number) => void;
  selectedUnitId: number | undefined;
  configuredUnits: number[];
};

export const WorkflowConfigUnitSelector = (props: PropsType) => {
  const { selectedUnitId, onUnitSelect, configuredUnits } = props;
  const { unitIdsWithWorkflows } = useWorkflows();

  const {
    orgTree,
    orgTreeState: { isUnitOpen, toggleUnit },
    handleUnitSelect,
  } = useWorkflowConfigUnitSelector({
    unitIdsWithWorkflows: [...unitIdsWithWorkflows, ...configuredUnits],
    onUnitSelect,
  });

  const resolveFolderIcon = useCallback((unitData: NestedOrgTreeUnit) => {
    const { hasConfigs, hasSubConfigs } = unitData;
    if (hasConfigs) {
      return <RuleFolder />;
    }
    if (hasSubConfigs) {
      return <Folder />;
    }
    return <FolderOpen />;
  }, []);

  const UnitItem = (props: { unit: NestedOrgTreeUnit }) => {
    const { unit } = props;
    const { unitId, subUnits, unitDepth, unitName, unitNumber } = unit;
    const isSelected = unitId === selectedUnitId;
    const hasSubUnits = subUnits.length > 0;
    const isVisible = isUnitOpen(unitId);

    return (
      <ListItem
        key={unitId}
        disablePadding
        sx={{ flexDirection: 'column', width: '100%' }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: hasSubUnits ? '1fr auto' : '100%',
          }}
        >
          <ListItemButton
            selected={isSelected}
            onClick={() => handleUnitSelect(unitId)}
          >
            <ListItemIcon>{resolveFolderIcon(unit)}</ListItemIcon>
            <ListItemText>
              <OrgUnitLabel
                name={unitName}
                number={unitNumber}
                variant={'inline'}
              />
            </ListItemText>
          </ListItemButton>
          {hasSubUnits && (
            <ListItemButton
              selected={isSelected}
              onClick={() => toggleUnit(unitId)}
            >
              <ListItemIcon sx={{ minWidth: '24px' }}>
                {isVisible ? <ExpandLess /> : <ExpandMore />}
              </ListItemIcon>
            </ListItemButton>
          )}
        </Box>
        {hasSubUnits && (
          <Collapse
            in={isVisible}
            key={`subUnits-${unitId}`}
            sx={{ width: '100%' }}
          >
            <List
              component="ul"
              disablePadding
              sx={{
                width: '100%',
                paddingLeft: theme.spacing(unitDepth > 0 ? 3 : 0),
              }}
            >
              {subUnits.map((subUnit) => (
                <UnitItem unit={subUnit} key={`unitItem-${subUnit.unitId}`} />
              ))}
            </List>
          </Collapse>
        )}
      </ListItem>
    );
  };

  return (
    <List>
      {orgTree.map((unit) => (
        <UnitItem unit={unit} key={`unitItem-${unit.unitId}`} />
      ))}
    </List>
  );
};
